"use client"

import { AlertTriangle, BellRing, Check, CheckCheck, ExternalLink, Mail } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import type {
  MessageBadgeIcon,
  MessageBadgeTone,
  MessageDelivery,
  MessageTrailer,
} from "@/lib/conversation/message-trailer"
import { localizeConversationDisplay } from "@/lib/i18n/conversation-copy"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"


/**
 * Delivery ticks, AI markers and the log link under a sent message — drawn the
 * same way wherever a conversation is read, from the descriptor its source built.
 */
export function ConversationMessageTrailer({ trailer }: { trailer: MessageTrailer }) {
  const tCopy = useTranslations("ConversationCopy")
  const tUi = useTranslations("Feedback.ConversationMessageTrailer")

  return (
    <>
      {trailer.delivery && <DeliveryTicks delivery={trailer.delivery} />}
      {trailer.badges.map((canonicalBadge) => {
        const badge = localizeConversationDisplay(canonicalBadge, tCopy)
        const Icon = badge.icon && BADGE_ICONS[badge.icon]
        return (
          <Badge
            key={badge.label}
            variant="outline"
            title={badge.hint}
            className={cn("px-1 py-0 text-[10px]", BADGE_TONES[badge.tone])}
          >
            {Icon && <Icon className="mr-0.5 h-2.5 w-2.5" />}
            {badge.label}
          </Badge>
        )
      })}
      {trailer.logId && (
        <a
          href={`?page=ai-logs&log_id=${trailer.logId}`}
          target="_blank"
          rel="noopener noreferrer"
          title={tUi("viewAILog")}
          className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ExternalLink className="h-2.5 w-2.5" />
          <span><span key={tUi("log")}>{tUi("log")}</span></span>
        </a>
      )}
    </>
  )
}

function DeliveryTicks({ delivery }: { delivery: MessageDelivery }) {
  switch (delivery) {
    case "read":
      return <CheckCheck className="h-3 w-3 text-blue-500" />
    case "delivered":
      return <CheckCheck className="h-3 w-3 text-muted-foreground" />
    case "sent":
      return <Check className="h-3 w-3 text-muted-foreground" />
  }
}

const BADGE_ICONS: Record<MessageBadgeIcon, typeof Mail> = {
  offline: Mail,
  "follow-up": BellRing,
  escalated: AlertTriangle,
}

const BADGE_TONES: Record<MessageBadgeTone, string> = {
  neutral: "",
  info: "border-blue-300 text-blue-600",
  warning: "border-amber-300 text-amber-600",
  danger: "border-red-300 text-red-600",
}
